import './i18n';
import './App.css';
import 'flag-icons/css/flag-icons.min.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Toaster } from './components/ui/sonner';
import { NavBar } from './components/NavBar';
import { HeroSection } from './components/HeroSection';
import { NewsSection } from './components/NewsSection';
import { LongevityPillars } from './components/LongevityPillars';
import { DigitalBook } from './components/DigitalBook';
import { ContactForm } from './components/ContactForm';
import { Footer } from './components/Footer';
import { CursorEffects } from './components/CursorEffects';
import { ClickSpark } from './components/ClickSpark';
import { SEOHead } from './components/SEOHead';
import ImprintPage from './pages/ImprintPage';
import PrivacyPage from './pages/PrivacyPage';
import GewinnspielPage from './pages/GewinnspielPage';
import ClaimPage from './pages/ClaimPage';

// Handles scroll-to-section when navigating back to home from sub-pages
const HomeScroller = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      // Short delay to let the page render first
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  return null;
};

const ResortSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="resort"
      style={{ padding: '100px 0', background: 'var(--color-surface-3)', position: 'relative' }}
      data-testid="resort-section"
    >
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }}>
          <div>
            <p className="section-label">{t('resort.label')}</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', marginBottom: '20px' }}>
              {t('resort.heading')}
            </h2>
            <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.8, marginBottom: '20px' }}>
              {t('resort.body1')}
            </p>
            <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.8, marginBottom: '32px' }}>
              {t('resort.body2')}
            </p>
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              {[
                { num: '40,000', key: 'stat_area' },
                { num: '2026',   key: 'stat_year' },
                { num: '4',      key: 'stat_langs' },
                { num: '10+',    key: 'stat_protocols' },
              ].map((stat, i) => (
                <div key={i}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'var(--color-gold)', fontWeight: 700 }}>
                    {stat.num}
                  </div>
                  <div style={{ fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-text-dim)' }}>
                    {t(`resort.${stat.key}`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/c9zunk2n_005.jpg"
              alt="Legend Spa Hotel Architecture"
              loading="lazy"
              style={{ width: '100%', height: '400px', objectFit: 'cover', borderRadius: '16px', boxShadow: 'var(--shadow-gold)' }}
            />
            <div style={{
              position: 'absolute', bottom: '20px', left: '20px',
              background: 'rgba(5,8,15,0.82)', border: '1px solid var(--color-border)',
              borderRadius: '10px', padding: '12px 18px', backdropFilter: 'blur(12px)',
            }}>
              <div style={{ fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--color-gold)' }}>
                {t('resort.badge_est')}
              </div>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', color: 'var(--color-text)' }}>
                {t('resort.badge_location')}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          #resort .container > div { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

const HomePage = () => {
  const audioRef = useRef(null);
  const [musicPlaying, setMusicPlaying] = useState(false);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (musicPlaying) {
      audio.pause();
      setMusicPlaying(false);
    } else {
      audio.play().then(() => setMusicPlaying(true)).catch(() => {});
    }
  };

  return (
    <div className="App">
      {/* Global persistent audio element */}
      <audio ref={audioRef} src="/legend-sound.mp3" preload="metadata" loop={false}
        onEnded={() => setMusicPlaying(false)} />
      <HomeScroller />
      <SEOHead />
      <NavBar onMusicToggle={toggleMusic} musicPlaying={musicPlaying} />
      <HeroSection />
      <NewsSection />
      <ResortSection />
      <LongevityPillars />
      <DigitalBook />
      <ContactForm />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <CursorEffects />
        <ClickSpark />
        <Toaster position="top-right" theme="dark" />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/imprint" element={<ImprintPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/gewinnspiel" element={<GewinnspielPage />} />
          <Route path="/claim" element={<ClaimPage />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
